import { FC } from 'react';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { Switch } from '@shadcn/ui/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shadcn/ui/components/ui/card';

export const FeatureFlagsPage: FC = () => {
    const { featureFlags, toggle } = useFeatureFlags();

    const organizationFlags = featureFlags.filter((flag) => flag.contextKind === 'organization');
    const userFlags = featureFlags.filter((flag) => flag.contextKind === 'user');

    const renderFlagSection = (flags: typeof featureFlags, title: string, description: string) => (
        <Card>
            <CardHeader>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {flags.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No feature flags available at the moment.</p>
                ) : (
                    flags.map((flag) => (
                        <div key={flag.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <h3 className={`font-medium ${flag.isLoading ? 'opacity-50' : ''}`}>
                                        {flag.label}
                                    </h3>
                                    {flag.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">{flag.id}</p>
                            </div>
                            <Switch
                                checked={flag.checked}
                                onCheckedChange={() => toggle(flag)}
                                disabled={flag.isLoading}
                                className="data-[state=checked]:bg-background-brand-primary data-[state=unchecked]:bg-input"
                            />
                        </div>
                    ))
                )}
            </CardContent>
        </Card>
    );

    return (
        <div className="space-y-6 max-w-4xl mx-auto">
            <div>
                <h1 className="text-2xl font-bold">Feature Flags</h1>
                <p className="text-muted-foreground mt-1">
                    Control feature availability and toggle experimental features on and off.
                </p>
            </div>

            {renderFlagSection(
                organizationFlags,
                'Workspace Features',
                'Organization-wide features that affect all users in your currently selected workspace.'
            )}

            {renderFlagSection(
                userFlags,
                'User Features',
                'Personal features that only affect your user in your currently selected workspace.'
            )}
        </div>
    );
};
